"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Search, SlidersHorizontal as Sliders, MapPin, Star, Code as Code2, TrendingUp, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
export default function Methodology() {
  const shouldReduceMotion = useReducedMotion();
  const steps = [{
    number: "01",
    title: "In-Depth SEO Audit",
    description: "I perform a comprehensive technical, architectural, and content audit to uncover hidden blockers and rapid growth opportunities.",
    icon: Search,
    highlight: "Identify technical & ranking barriers"
  }, {
    number: "02",
    title: "On-Page & Technical Optimization",
    description: "Directly fixing site speed, crawlability, internal linking, and optimizing target pages for true search intent.",
    icon: Sliders,
    highlight: "Pillar content & semantic mapping"
  }, {
    number: "03",
    title: "Citations & Entity Alignment",
    description: "Building consistent NAP signals, entity authority, and trusted directory presence for local and national footprint.",
    icon: MapPin,
    highlight: "Strong foundation for search & AI"
  }, {
    number: "04",
    title: "Review & Reputation Strategy",
    description: "Systematizing review collection to enhance organic search conversion rates and build authentic brand equity.",
    icon: Star,
    highlight: "Trust signals that boost clicks"
  }, {
    number: "05",
    title: "Advanced Schema & Structured Data",
    description: "Deploying custom JSON-LD schema markup so Google and modern AI search engines accurately understand your offer.",
    icon: Code2,
    highlight: "Rich snippets & AI Search visibility"
  }, {
    number: "06",
    title: "Transparent Measurement & Reporting",
    description: "Direct communication with clear monthly insights focusing on ROI, lead volume, key rankings, and revenue growth.",
    icon: TrendingUp,
    highlight: "No vanity metrics — clear business impact"
  }];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="methodologystepprocess" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 -right-24 size-96 rounded-full bg-secondary/30 blur-3xl opacity-50" />
        <div className="absolute -bottom-24 -left-24 size-96 rounded-full bg-accent/20 blur-3xl opacity-40" />
      </div>

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center mb-16 lg:mb-20">
          <Badge variant="secondary" className="mb-4 px-3.5 py-1 text-xs uppercase tracking-wider font-semibold bg-secondary text-secondary-foreground border-border/50">
            Proven Process
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground font-serif">
            My 6-Step SEO Methodology for Sustainable Search Growth
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto font-sans leading-relaxed">
            No fluff or generic agency templates. You work directly with me through every phase of this structured roadmap to build long-term organic authority.
          </p>
        </div>

        <div className="relative">
          <div aria-hidden="true" className="hidden lg:block absolute top-1/2 left-8 right-8 h-0.5 -translate-y-12 bg-gradient-to-r from-accent/10 via-accent/40 to-accent/10 z-0 pointer-events-none" />

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: "-50px"
        }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 relative z-10">
            {steps.map((step, i) => {
            const IconComponent = step.icon;
            return <motion.div key={step.number} variants={itemVariants} data-index={i}>
                  <Card className="h-full border border-border/80 bg-card/80 backdrop-blur-xs text-card-foreground shadow-sm hover:shadow-md transition-[box-shadow,border-color] duration-300 hover:border-primary/40 flex flex-col justify-between group">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center justify-center size-12 rounded-xl bg-primary text-primary-foreground font-bold text-lg shadow-sm group-hover:scale-105 transition-transform duration-300">
                          {step.number}
                        </div>

                        <div className="p-2.5 rounded-lg bg-muted text-foreground border border-border/50">
                          <IconComponent className="size-5 text-primary" />
                        </div>
                      </div>

                      <CardTitle className="text-xl font-bold font-serif text-card-foreground group-hover:text-primary transition-colors">
                        {step.title}
                      </CardTitle>
                    </CardHeader>

                    <CardContent className="space-y-4 flex-1 flex flex-col justify-between pt-0">
                      <CardDescription className="text-muted-foreground text-sm leading-relaxed font-sans">
                        {step.description}
                      </CardDescription>

                      <div className="pt-3 border-t border-border/60 flex items-center gap-2 text-xs font-medium text-foreground/90">
                        <CheckCircle2 className="size-4 text-accent shrink-0" />
                        <span>{step.highlight}</span>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>;
          })}
          </motion.div>
        </div>

        <div className="mt-16 lg:mt-20 rounded-2xl bg-muted/60 border border-border p-8 sm:p-10 lg:p-12 text-center max-w-4xl mx-auto shadow-xs">
          <h3 className="text-2xl sm:text-3xl font-bold font-serif text-foreground">
            Ready to scale your search visibility with a personal expert?
          </h3>
          <p className="mt-3 text-muted-foreground text-base max-w-xl mx-auto font-sans">
            Let us review your current site performance together and outline a tailored roadmap for your specific business goals.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild size="lg" className="w-full sm:w-auto font-medium shadow-sm">
              <Link to="/ContactPage">
                Book a Free SEO Consultation
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto font-medium">
              <Link to="/CaseStudiesPage">
                Explore My Case Studies
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}